import { Box, Button, Divider, styled, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { createFileRoute, useSearch, useNavigate } from '@tanstack/react-router'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AndroidIcon from 'icons/Technologies/Android.icon';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Route as AdminInterviewFromScratchRoute } from './_admin.admin-interview-from-scratch'
import useMainStore from '../../store/useMainStore'
import dayjs, { Dayjs } from 'dayjs'

interface Interview {
    id: string;
    name: string;
    createdOn: string;
    // Add other interview properties as needed
}

export const Route = createFileRoute(
  '/(admin)/_admin/admin-create-interview-step-2',
)({
  component: RouteComponent,
})


const AdvancedInterview = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#fff',
    boxShadow: '0px 1px 2px 0px #0205111A',
    border: '1px solid var(--Light-Gray)',
    borderRadius: '12px',
    padding: '24px',
    width: 'fit-content'
})

const FromScratchInterview = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'var(--Light-Gray)',
    boxShadow: '0px 1px 2px 0px #0205111A',
    border: '1px solid var(--Light-Gray)',
    borderRadius: '12px',
    padding: '24px',
    width: 'fit-content'
})

function RouteComponent() {
  const search = useSearch({ from: '/(admin)/_admin/admin-create-interview-step-2' });
  const navigate = useNavigate();
  const inviteDeadlineISO = useMainStore.use.inviteDeadlineISO()
  const setInviteDeadlineISO = useMainStore.use.setInviteDeadlineISO()
  const category = (search as any).category;
  const techId = (search as any).techId;

  // Hide sections based on category
  const shouldHideFromScratch = category === 'all-technologies';
  const shouldHideAdvancedGenerator = category === 'main-organization';

  // Fetch interviews data when techId is available
  const { data: interviews, isLoading: isLoadingInterviews, error: errorInterviews } = useQuery({
    queryKey: ['interviews', techId],
    queryFn: async () => {
      if (!techId) return [];
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://interviewer.runasp.net/api/technologies/${techId}/interviews`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data as Interview[];
    },
    enabled: !!techId,
  });

  const handleAdvancedGeneratorClick = () => {
    navigate({
      to: '/admin-advanced-interview-generator-step-1',
      search: {
        techId: techId
      }
    });
  };

  const handleFromScratchClick = () => {
    navigate({ to: AdminInterviewFromScratchRoute.to });
  };

  const handleDeadlineChange = (value: Dayjs | null) => {
    setInviteDeadlineISO(value ? value.startOf('day').toDate().toISOString() : null)
  }

  const handlePredefinedInterviewClick = (_interviewId: string) => {
    navigate({ to: AdminInterviewFromScratchRoute.to });
  };

  return (
    <Box sx={{maxWidth: '80%', mx: 'auto'}}>
        <Typography variant='h1' sx={{pt: '48px', textAlign: 'center', pb: '24px'}}>Add some essential information</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker 
                label="Interview deadline (MM/DD/YY)"
                value={inviteDeadlineISO ? dayjs(inviteDeadlineISO) : null}
                minDate={dayjs().add(1, 'day').startOf('day')}
                onChange={handleDeadlineChange}
            />
        </LocalizationProvider>
        <Typography variant='h2' sx={{pt: '48px', textAlign: 'center', pb: '16px'}}>Let's start from somewhere</Typography>
        <Box sx={{display: 'flex', gap: '20px'}}>
            {!shouldHideAdvancedGenerator && (
                <AdvancedInterview>
                    <AndroidIcon />
                    <Typography variant='h2' sx={{lineHeight: '32px'}}>Do you want to use our advanced interview generator?</Typography>
                    <Typography variant='body1' sx={{color: 'var(--Interviewer-Lighter-Black)', pb: '24px', pt: '12px'}}>A short description about advanced interview generator goes here.</Typography>
                    <Button 
                        variant="contained" 
                        color="primary"
                        onClick={handleAdvancedGeneratorClick}
                    >
                        Use advanced interview generator
                    </Button>
                </AdvancedInterview>
            )}
            {!shouldHideFromScratch && (
                <FromScratchInterview>
                    <AndroidIcon />
                    <Typography variant='h2' sx={{lineHeight: '32px'}}>Make your interview from scratch</Typography>
                    <Typography variant='body1' sx={{color: 'var(--Interviewer-Lighter-Black)', pb: '24px', pt: '12px'}}>A short description about making an interview from scratch here.</Typography>
                    <Button variant="contained" color="primary" onClick={handleFromScratchClick}>Make an interview from scartch</Button>
                </FromScratchInterview>
            )}
        </Box>
        <Divider 
            orientation="horizontal" 
            sx={{borderColor: 'var(--Interviewer-Near-White)',
            my: '56px'}} 
        />

        {!shouldHideFromScratch && (
            <>
                <Typography variant='h2' sx={{pb: '32px', textAlign: 'center'}}>Other Android Interviews already created.</Typography>

                {isLoadingInterviews && <Typography>Loading interviews...</Typography>}
                {errorInterviews && <Typography color="error">Error loading interviews</Typography>}
                {interviews?.map((interview) => (
                    <Box key={interview.id} sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        border: '1px solid var(--Light-Gray)',
                        borderRadius: '12px',
                        padding: '12px',
                        mb: '16px',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'var(--Interviewer-Near-White)' }
                    }} onClick={() => handleFromScratchClick()}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                            <AndroidIcon />
                            <Typography variant='h4' sx={{fontWeight: '600'}}>{interview.name}</Typography>
                        </Box>

                        <Box sx={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <Typography variant='h5' sx={{color: 'var(--Interviewer-Lightest-Black)', fontWeight: '600'}}>Created on</Typography>
                                <Typography variant='h4' sx={{color: 'var(--Interviewer-Black)', fontWeight: '600'}}>
                                    {new Date(interview.createdOn).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric' 
                                    })}
                                </Typography>
                            </Box>
                            <KeyboardArrowRightIcon />
                        </Box>
                    </Box>
                ))}
            </>
        )}
    </Box>
  )
}
